'use client'

import { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NinxlEvervaultShell } from './ninxl-evervault-shell'

/** Govts section divider: official seal strip */
export const NinxlGovtsSealDivider = memo(function NinxlGovtsSealDivider({
  label = 'OFFICIAL',
}: {
  label?: string
}) {
  return (
    <div className="ninxl-govts-seal-divider mx-auto flex max-w-5xl items-center gap-4 px-6 py-5" aria-hidden>
      <div className="ninxl-govts-seal-line flex-1" />
      <span className="ninxl-8bit-label ninxl-theme-text-muted tracking-[0.24em]">{label}</span>
      <div className="ninxl-govts-seal-line flex-1" />
    </div>
  )
})

/** Challenge dossier: vertical manifest, not card grid */
export const NinxlGovtsDossierManifest = memo(function NinxlGovtsDossierManifest({
  items,
  className,
}: {
  items: readonly { ref: string; title: string; desc: string; icon: LucideIcon }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className="ninxl-govts-dossier-shell ninxl-pixel-border relative overflow-hidden">
      <NinxlEvervaultShell size="md" contentClassName="p-0">
        <div className={cn('ninxl-govts-dossier divide-y-2 divide-[color:var(--ninxl-8bit-dim)]', className)}>
          {items.map((item, index) => (
            <motion.div
              key={item.ref}
              className="group flex gap-4 p-5 md:gap-6 md:p-6"
              initial={shouldReduce ? false : { opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <div className="ninxl-govts-dossier-ref shrink-0 pt-0.5">
                <div className="ninxl-8bit-label text-[10px]">{item.ref}</div>
                <div className="ninxl-govts-dossier-icon mt-3 flex h-10 w-10 items-center justify-center">
                  <item.icon className="h-5 w-5" aria-hidden />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="ninxl-theme-text mb-2 text-base font-bold md:text-lg [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]">
                  {item.title}
                </h3>
                <p className="ninxl-theme-text-muted text-sm leading-relaxed [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]">
                  {item.desc}
                </p>
              </div>
              <div className="ninxl-govts-dossier-stamp hidden shrink-0 self-start px-2 py-1 sm:block">
                <span className="ninxl-8bit-label text-[8px] [text-shadow:0_1px_2px_var(--ninxl-theme-frame,#fff)]">
                  FLAGGED
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </NinxlEvervaultShell>
    </div>
  )
})

/** Perimeter stack: layered security model */
export const NinxlGovtsPerimeterStack = memo(function NinxlGovtsPerimeterStack({
  layers,
  className,
}: {
  layers: readonly { tier: string; title: string; desc: string }[]
  className?: string
}) {
  return (
    <div className={cn('space-y-3', className)}>
      {layers.map((layer, index) => (
        <div
          key={layer.tier}
          className="ninxl-govts-layer ninxl-pixel-border relative p-4 md:p-5"
          style={{ marginLeft: `${index * 12}px`, marginRight: `${(layers.length - 1 - index) * 12}px` }}
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="ninxl-govts-layer-tier ninxl-8bit-label">{layer.tier}</span>
            <span className="ninxl-8bit-label ninxl-theme-text-muted text-[9px]">LAYER {index + 1}</span>
          </div>
          <h3 className="ninxl-theme-text mb-1 text-sm font-bold md:text-base">{layer.title}</h3>
          <p className="ninxl-theme-text-muted text-xs leading-relaxed md:text-sm">{layer.desc}</p>
        </div>
      ))}
    </div>
  )
})

/** Citizen case queue: horizontal ticket lane */
export const NinxlGovtsCitizenQueue = memo(function NinxlGovtsCitizenQueue({
  steps,
  className,
}: {
  steps: readonly { step: string; detail: string; status: string }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className={cn('relative', className)}>
      <div className="ninxl-govts-queue-track mb-3 hidden h-px md:block" aria-hidden />
      <div className="flex gap-4 overflow-x-auto pb-2 md:gap-5">
        {steps.map((item, index) => (
          <motion.div
            key={item.step}
            className="ninxl-govts-queue-ticket ninxl-pixel-border min-w-[200px] shrink-0 p-4 md:min-w-[220px]"
            initial={shouldReduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-24px' }}
            transition={{ duration: 0.45, delay: index * 0.07 }}
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="ninxl-8bit-label text-[9px]">TICKET-{String(index + 1).padStart(2, '0')}</span>
              <span className="ninxl-govts-status-pill">{item.status}</span>
            </div>
            <div className="ninxl-theme-text mb-2 text-sm font-bold">{item.step}</div>
            <p className="ninxl-theme-text-muted text-xs leading-relaxed">{item.detail}</p>
          </motion.div>
        ))}
      </div>
      <p className="ninxl-8bit-label ninxl-theme-text-muted mt-4 text-center text-[10px]">
        Scroll the queue · every ticket stays inside the national perimeter
      </p>
    </div>
  )
})

/** Agency roster: horizontal department scroll */
export const NinxlGovtsAgencyRoster = memo(function NinxlGovtsAgencyRoster({
  agencies,
  className,
}: {
  agencies: readonly { code: string; title: string; desc: string; icon: LucideIcon }[]
  className?: string
}) {
  return (
    <div className={cn('-mx-2 overflow-x-auto px-2 pb-2', className)}>
      <div className="flex min-w-max gap-4 md:gap-5">
        {agencies.map((agency) => (
          <div key={agency.code} className="ninxl-govts-agency-tile ninxl-pixel-border w-[240px] shrink-0 p-5 md:w-[260px]">
            <div className="mb-4 flex items-start justify-between gap-3">
              <span className="ninxl-govts-agency-code">{agency.code}</span>
              <div className="ninxl-govts-dossier-icon flex h-9 w-9 items-center justify-center">
                <agency.icon className="h-4 w-4" aria-hidden />
              </div>
            </div>
            <h3 className="ninxl-theme-text-accent mb-2 text-sm font-bold">{agency.title}</h3>
            <p className="ninxl-theme-text-muted text-xs leading-relaxed">{agency.desc}</p>
          </div>
        ))}
      </div>
    </div>
  )
})

/** Compliance ledger: numbered policy entries */
export const NinxlGovtsComplianceLedger = memo(function NinxlGovtsComplianceLedger({
  entries,
  className,
}: {
  entries: readonly { code: string; title: string; desc: string; icon: LucideIcon }[]
  className?: string
}) {
  return (
    <div className={cn('grid gap-3 md:grid-cols-2', className)}>
      {entries.map((entry) => (
        <div key={entry.code} className="ninxl-govts-ledger-row ninxl-pixel-border flex gap-4 p-4">
          <div className="ninxl-govts-ledger-code shrink-0">{entry.code}</div>
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <entry.icon className="ninxl-theme-text-accent h-4 w-4 shrink-0" aria-hidden />
              <h3 className="ninxl-theme-text text-sm font-bold">{entry.title}</h3>
            </div>
            <p className="ninxl-theme-text-muted text-xs leading-relaxed">{entry.desc}</p>
          </div>
        </div>
      ))}
    </div>
  )
})

export const NinxlGovtsSectionBackdrop = memo(function NinxlGovtsSectionBackdrop({
  variant,
  className,
}: {
  variant: 'dossier' | 'perimeter' | 'roster' | 'ledger'
  className?: string
}) {
  const washes: Record<typeof variant, string> = {
    dossier: 'from-rose-500/[0.03] via-transparent to-amber-500/[0.02]',
    perimeter: 'from-[#21728d]/[0.06] via-transparent to-transparent',
    roster: 'from-emerald-500/[0.03] via-transparent to-[#21728d]/[0.02]',
    ledger: 'from-violet-500/[0.03] via-transparent to-transparent',
  }

  return (
    <div
      className={cn('pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br', washes[variant], className)}
      aria-hidden
    />
  )
})